/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { WalletSelectedDesign } from "@lemmonhq/types";

/**
 * The pass as a branded card: the design's artwork, colours and icon, with
 * the program and place names. Program-level only; the install page shows no
 * member data.
 */
export type PassPreviewProps = {
  kind: "membership" | "coin";
  programName: string;
  placeName: string;
  design: WalletSelectedDesign | null;
};

const HEX = /^#[\da-f]{6}$/i;

export const PassPreview = defineComponent<PassPreviewProps>({
  name: "PassPreview",
  props: ["kind", "programName", "placeName", "design"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const brand = props.design?.document.brand;
      const urls = props.design?.asset_urls ?? {};
      const asset = (id: string | undefined) => (id ? urls[id] : undefined);
      const artwork = asset(brand?.assets.artwork?.asset_id);
      const icon = asset(
        brand?.assets.icon?.asset_id ?? brand?.assets.logo?.asset_id,
      );
      // Design colours are validated hex; anything else keeps the theme look.
      const color = (value: string | undefined) =>
        value && HEX.test(value) ? value : undefined;
      const background = color(brand?.colors.background);
      const foreground = color(brand?.colors.foreground);
      const label = color(brand?.colors.label);

      return (
        <div
          class="overflow-hidden rounded-3xl bg-neutral text-neutral-content shadow-xl ring-1 ring-base-content/5"
          style={{ backgroundColor: background, color: foreground }}
        >
          {artwork ? (
            <div class="relative aspect-[4/3] w-full">
              <img
                src={artwork}
                alt=""
                class="absolute inset-0 h-full w-full object-cover"
              />
              <div
                class="absolute inset-x-0 bottom-0 h-1/2"
                style={{
                  backgroundImage: `linear-gradient(to bottom, transparent, ${
                    background ?? "hsl(var(--n))"
                  })`,
                }}
              />
            </div>
          ) : null}
          <div class="flex items-center gap-3 p-5">
            {icon ? (
              <img
                src={icon}
                alt=""
                class="h-12 w-12 shrink-0 rounded-xl object-cover"
              />
            ) : null}
            <div class="min-w-0">
              <div
                class="text-xs font-semibold uppercase tracking-widest opacity-70"
                style={{ color: label }}
              >
                {props.kind === "coin"
                  ? t("customer.wallet_install.rewards")
                  : t("customer.wallet_install.membership")}
              </div>
              <div class="truncate text-xl font-bold">{props.programName}</div>
              {props.placeName !== props.programName ? (
                <div class="truncate text-sm opacity-70">{props.placeName}</div>
              ) : null}
            </div>
          </div>
        </div>
      );
    };
  },
});
